import { LitElement, html, css } from 'lit-element';
import { HeaderStyles } from '../styles/header-styles';
import '@trumedianetworks/tmn-core-components/src/tmn-grid';
import '@trumedianetworks/tmn-core-components/src/tmn-button';
import '@trumedianetworks/tmn-core-components/src/tmn-radio';
import '@trumedianetworks/tmn-core-components/src/tmn-tabs';
import '@trumedianetworks/tmn-core-components/src/tmn-icon';
import '@trumedianetworks/tmn-core-components/src/tmn-form-group';
import '../util/copy-input';
import { AUTHORIZATIONS } from '../authorizations/authorizations';
import dayjs from 'dayjs';

const { CAN_MANAGE_MASTER_TOKENS } = AUTHORIZATIONS;

class SiteTokenDetail extends LitElement {
    constructor() {
        super();
    }
    static get styles() {
        return [
            HeaderStyles.styles,
            css`
                :host {
                    max-width: 800px;
                    background: white;
                    display: flex;
                    flex-flow: column;
                    padding: 8px;
                    background-color: var(--app-surface-color);
                    flex: 1 1 100%;
                    min-width: 535px;
                    min-height: 550px;
                }

                @media (max-width: 1024px) {
                    :host {
                        min-width: 0;
                    }
                }
                input {
                    display: flex;
                    flex: 1;
                    background: var(--light-gray-100);
                    padding: 4px;
                    border: 1px solid var(--light-gray-200);
                }
                tmn-form-group label {
                    font-weight: 600;
                }
                tmn-form-group {
                    flex: 1;
                }
                .date-group {
                    align-items: flex-end;
                }
                .date-group tmn-form-group {
                    width: 130px;
                    flex: 0;
                }
                .date-group > tmn-icon-button {
                    margin: 0 8px 0 4px;
                }
                .date-group > tmn-button {
                    margin-left: 6px;
                }
                .date-group > tmn-icon {
                    flex: 0;
                    margin-bottom: 7px;
                    margin-right: 6px;
                }
            `,
        ];
    }

    static get properties() {
        return {
            validationErrors: {
                type: Object,
            },
            passedValidations: {
                type: Object,
            },
            tokenData: {
                type: Object,
            },
            isSaving: {
                type: Boolean,
            },
            isLoading: {
                type: Boolean,
            },
            authorizations: {
                type: Array,
            },
            // if we ever change more than date, make this stagededits
            expirationDate: {
                type: String,
            },
        };
    }
    updated(changedProps) {
        // for now, just manage the state in this component since it isn't really shared
        // with the rest of the app

        if (changedProps.has('tokenData')) {
            this.expirationDate = this.tokenData?.expiration_date;
        }
    }

    render() {
        if (!this.authorizations.includes(CAN_MANAGE_MASTER_TOKENS)) {
            return 'Token management not allowed';
        }
        return html`<div class="fixed-header">${this.renderHeader()}</div>
            ${this.renderMain()}`;
    }

    renderMain() {
        return this.renderForm();
    }

    renderHeader() {
        return html`<h2>Token Details</h2>`;
    }

    renderStatusMessages() {
        const { isLoading, isSaving } = this;
        if (isLoading) {
            return html`<div>Loading...</div>`;
        }
        if (isSaving) {
            return html`<div>Saving...</div>`;
        }
    }

    renderForm() {
        const { isLoading, isSaving } = this;
        if (isLoading || isSaving) {
            return this.renderStatusMessages();
        }
        // for now, only edit mode
        return this.renderEditForm();
    }

    renderEditForm() {
        const { tokenData = {}, validationErrors, expirationDate } = this;
        const { name, token, kid, is_expired, last_used, ts } = tokenData;
        if (!token) {
            return;
        }
        return html`<tmn-grid
            container
            direction="column"
            justify="center"
            alignContent="center"
            spacing="2"
            @keydown=${this.handleEnter}>
            <tmn-grid item>
                <tmn-form-group>
                    <label>Site:</label>
                    ${name}
                </tmn-form-group>
            </tmn-grid>
            <tmn-grid item container>
                <tmn-form-group>
                    <label>Token:</label>
                    <copy-input .value=${token}></copy-input>
                </tmn-form-group>
            </tmn-grid>
            <tmn-grid item class="date-group">
                ${is_expired
                    ? html`<tmn-icon
                          title="expired"
                          color="error"
                          icon="event_busy"
                          iconvariant="outlined"></tmn-icon>`
                    : null}
                <tmn-form-group>
                    <label>Expiration date</label>
                    <tmn-date-picker
                        .error=${validationErrors}
                        .value=${expirationDate}
                        .monthsDisplayCount=${1}
                        .required=${false}
                        @tmn-date-selected=${this.handleSelectDate}
                        @tmn-tool-closed=${this.handleDatePickerClosed}>
                    </tmn-date-picker>
                </tmn-form-group>
                <tmn-button @click="${this.handleClearDate}">Clear </tmn-button>
                <tmn-button @click=${this.handleExpireNow}>Expire Now</tmn-button>
            </tmn-grid>
            <tmn-grid item>
                <tmn-form-group>
                    <label>Key id</label>
                    <input value="$${kid}" />
                </tmn-form-group>
            </tmn-grid>
            <tmn-grid item container alignItems="flex-start" direction="column">
                <tmn-form-group>
                    <label>Last used:</label>
                    ${last_used}
                </tmn-form-group>
            </tmn-grid>
            <tmn-grid item container alignItems="flex-start" direction="column">
                <tmn-form-group>
                    <label>Created:</label>
                    ${ts}
                </tmn-form-group>
            </tmn-grid>
            <tmn-grid item container spacing="1">
                <tmn-grid item>
                    <tmn-button color="primary" @click=${this.handleSaveTokenData}>Save</tmn-button>
                </tmn-grid>
                <tmn-grid item>
                    <tmn-button color="primary" @click=${this.handleCancel}>Cancel</tmn-button>
                </tmn-grid>
            </tmn-grid>
        </tmn-grid> `;
    }

    handleSaveTokenData(e) {
        const { tokenData, expirationDate } = this;
        const { token } = tokenData;
        const event = new CustomEvent('token-edit-save', {
            detail: {
                token,
                expiration_date: expirationDate,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    handleCancel() {
        const { tokenData } = this;
        const event = new CustomEvent('token-edit-cancel', {
            detail: {
                token: tokenData?.token,
            },
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }

    handleSelectDate(e) {
        this.expirationDate = e.detail?.value;
    }

    handleClearDate(e) {
        this.expirationDate = null;
    }
    handleExpireNow(e) {
        this.expirationDate = dayjs().format('YYYY-MM-DD');
    }

    handleDatePickerClosed(e) {
        const composedEventPath = e.composedPath() || [];
        const target = composedEventPath[0];
        this.expirationDate = target.value;
    }
}

customElements.define('site-token-detail', SiteTokenDetail);
